import { Box, Divider, ITheme, SvgIcon, Typography, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../../store/reducers/store';
import { Utilities } from '../../../utility/utils';
import { ReactComponent as slidersIcon } from '../../../assets/sliders.svg';
import { ReactComponent as camembertIcon } from '../../../assets/camembert.svg';
import { ReactComponent as worldIcon } from '../../../assets/world.svg';
import FundAllocationGraph from '../../common/FundAllocationGraph';
import { PortfolioFundAllocation, SectorAffectation } from '../../../models/Portfolio';
import LeoTag from '../../common/LeoTag';
import { FundItem } from '../../../models/Fund';
import StageDistributionGraph from '../../common/StageDistributionGraph';
import { GeographicDistribution, StageDistribution } from '../../../models/Common';
import GeographicDistributionGraph from '../../common/GeographicDistributionGraph';
import { useTranslation } from 'react-i18next';
import useIsFullable from '../../../hooks/useIsFullable';
import LeoAiOverlay from '../../common/LeoAiOverlay';

const FundAllocationProjection: React.FC = () => {
    const theme = useTheme() as ITheme;
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    const funds = useAppSelector(state => state.libraryOfFundsReducer.funds);
    const { t } = useTranslation();
    const isFullable = useIsFullable();
    const headerRef = useRef<HTMLDivElement>(null);
    const [showAiToolTip, setShowAiToolTip] = useState(false);

    useEffect(() => {
        setShowAiToolTip(!isFullable && headerRef.current !== null);
    }, [isFullable, headerRef.current]);

    if (portfolio === undefined || funds === undefined) {
        return <></>;
    }

    const selectedFunds = portfolio.funds.map(sf => {
        const f = funds.find(f => f.id === sf.id);
        if (f) {
            return { fund: f, proportion: sf.proportion };
        } else {
            return undefined;
        }
    }).filter(f => f !== undefined) as { fund: FundItem, proportion: number }[];

    const allocation = selectedFunds.map(a => ({ name: a.fund.name, logo: a.fund.logo, amount: a.proportion * portfolio.fundraisingTarget })) as PortfolioFundAllocation[];
    const totalAllocated = selectedFunds.reduce((t, i) => t + i.proportion * portfolio.fundraisingTarget, 0);
    const unfullyAllocated = totalAllocated < portfolio.fundraisingTarget;
    if (unfullyAllocated) {
        allocation.push({ id: 0, name: "Unallocated", amount: portfolio.fundraisingTarget - totalAllocated, logo: '' });
    }
    const sectors: SectorAffectation[] = [];
    const stage: StageDistribution = { early: 0, growth: 0, late: 0 };
    const geo: GeographicDistribution[] = [];
    for (const fund of selectedFunds) {
        for (const sector of fund.fund.sectorDistribution) {
            const index = sectors.findIndex(s => s.name === sector.sector);
            if (index === -1) {
                sectors.push({ icon: sector.icon, name: sector.sector, proportion: sector.proportion * fund.proportion, id: 0 });
            } else {
                sectors[index].proportion += sector.proportion * fund.proportion;
            }
        }
        for (const distrib of fund.fund.geographicDistribution) {
            const index = geo.findIndex(s => s.zone === distrib.zone);
            if (index === -1) {
                geo.push({ icon: distrib.icon, zone: distrib.zone, proportion: distrib.proportion * fund.proportion });
            } else {
                geo[index].proportion += distrib.proportion * fund.proportion;
            }
        }
        stage.early += fund.fund.stageDistribution.early * fund.proportion;
        stage.growth += fund.fund.stageDistribution.growth * fund.proportion;
        stage.late += fund.fund.stageDistribution.late * fund.proportion;
    }
    sectors.sort((a, b) => b.proportion - a.proportion)
    geo.sort((a, b) => b.proportion - a.proportion)
    return <Box display={"flex"} flexDirection={"column"} bgcolor={theme.palette.common.white} padding={"12px 26px"} borderRadius={"8px"} gap={"21px"}>
        <Box height={"22px"} marginTop={"-29px"} display={"flex"} gap={"4px"}>
            {portfolio.retailInvestors && <Box height={"22px"} width={"auto"} padding={"0 3px"} bgcolor={theme.palette.primary.dark} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('LibraryOfFundsELTIF')}</Typography>
            </Box>}
        </Box>
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} ref={headerRef}>
            <Typography fontSize={"24px"} lineHeight={"29.26px"} fontWeight={"700"}>{portfolio.name}</Typography>
            <Box display={"flex"} flexDirection={"column"} alignItems={"flex-end"} gap={"6px"}>
                <Typography fontSize={"22px"} lineHeight={"26.82px"} fontWeight={"600"}>€{Utilities.toFriendlyMoneyValue(portfolio.fundraisingTarget)}</Typography>
                <Typography fontSize={"12px"} lineHeight={"10px"} fontWeight={"600"}>{t('PortfolioPageItemFundraisingTarget')}</Typography>
            </Box>
        </Box>
        <Divider sx={{ margin: "0 -26px" }} />
        <FundAllocationGraph allocations={allocation} bigFormat />
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={slidersIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemSectorDistribution')}</Typography>
        </Box>
        <Divider />
        <Box display={"flex"} gap={"12px"} flexWrap={"wrap"}>
            {sectors.map(s => <LeoTag key={`sector-${s.name}`} text={s.name} value={s.proportion} icon={s.icon} size='medium' />)}
        </Box>
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={camembertIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemStageDistribution')}</Typography>
        </Box>
        <Divider />
        <StageDistributionGraph data={stage} />
        <Box display={"flex"} gap={"17px"}>
            <SvgIcon component={worldIcon} inheritViewBox width={"20"} />
            <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"500"}>{t('PortfolioPageItemGeographicDistribution')}</Typography>
        </Box>
        <Divider />
        <GeographicDistributionGraph distribution={geo} />
        {showAiToolTip && <LeoAiOverlay target={headerRef}>
            <p>{t('CreatePortfolioAiAssistantGoBack')}</p>
        </LeoAiOverlay>}
    </Box>
}

export default FundAllocationProjection